import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Step0 from './Step0';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step3Additional from './Step3Additional';
import Step4 from './Step4';
import Step5 from './Step5';
import KitBOM from './KitBOM';
import Modal from '../../shared/modal';

import { setFormStep } from '../../../actions/casesActions';
import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getProcedures, getProcedureOptions, getProcedureEquipmentLists } from '../../../actions/proceduresActions';
import { getProducts } from '../../../actions/productsActions';
import { getKits } from '../../../actions/kitsActions';
import { getItems, getItemsByIds, getItemsByProducts } from '../../../actions/itemsActions';
import { getFlows } from '../../../actions/flowsActions';
import { getSets } from '../../../actions/setsActions';
import { getKitPreferenceByParameters } from '../../../actions/kitPreferencesActions';

import { useLoading } from '../../../hooks';

import userRoles from '../../../constants/userRoles';
import { kitPreferenceItemTypes, kitVariantTypes, procedureEquipmentListItemTypes } from '../../../constants/enums';

const CaseModal = (props) => {
  const {
    open,
    onClose,
    activeCase = {},
    mode = 'create',
    surgeons,
    hospitals,
    procedures,
    procedureOptions,
    products,
    kits,
    flows = [],
    flowSteps = [],
    sets = [],
    userRole,
    userAddresses,
    onSubmit,
    editingBy,
    currentTenant,
    connectedTenantId,
  } = props;

  const dispatch = useDispatch();

  const step = useSelector((state) => state?.activeCase?.formStep);
  const connectedTenants = useSelector((state) => state?.user?.currentUser?.connectedTenants) || [];
  const tenants = useSelector((state) => state?.tenant?.list) || [];
  const currentUser = useSelector((state) => state.user.currentUser);
  const tenantColor = useSelector((state) => state?.tenant?.currentTenant?.colorPrimary);

  const setStep = (newStep) => dispatch(setFormStep(newStep));

  const [items, setItems] = useState([]);

  const [connectedSurgeons, setConnectedSurgeons] = useState([]);
  const [connectedHospitals, setConnectedHospitals] = useState([]);
  const [connectedProcedures, setConnectedProcedures] = useState([]);
  const [connectedProcedureOptions, setConnectedProcedureOptions] = useState([]);
  const [connectedProducts, setConnectedProducts] = useState([]);
  const [connectedKits, setConnectedKits] = useState([]);
  const [connectedItems, setConnectedItems] = useState([]);
  const [connectedFlows, setConnectedFlows] = useState([]);
  const [connectedSets, setConnectedSets] = useState([]);

  const [fetching, setFetching] = useState(false)
  const [selected, setSelected] = useState(currentTenant?.id);
  const [selectedKit, setSelectedKit] = useState(null);

  const [kitPreference, setKitPreference] = useState(null);
  const [kitPreferenceItems, setKitPreferenceItems] = useState([]);
  const [kitPreferenceLoading, setKitPreferenceLoading] = useState(false);
  const [additionalItems, setAdditionalItems] = useState([]);
  const [procedureEquipmentLists, setProcedureEquipmentLists] = useState([]);
  const [equipmentListItems, setEquipmentListItems] = useState([]);

  const [formData, setFormData] = useState({});

  const { loading, startLoading, stopLoading } = useLoading(false);

  useEffect(() => {
    if (mode === 'update' && activeCase && open && !formData?.deliveryAddress) {
      setFormData({
        ...activeCase,
        deliveryAddress: activeCase.deliveryAddress ? activeCase.deliveryAddress.id : null,
      });
    }
  }, [activeCase, open]);

  useEffect(() => {
    if (userRole === userRoles.SALES_REP.name && connectedTenantId) {
      setSelected(connectedTenantId);
      fetchConnectedData(connectedTenantId).catch();
    }
  }, [userRole, connectedTenantId]);

  const fetchConnectedData = async (tenantId) => {
    const conSurgeons = await dispatch(getSurgeons(tenantId));
    const conHospitals = await dispatch(getHospitals(tenantId));
    const conProcedures = await dispatch(getProcedures(tenantId));
    const conProcedureOptions = await dispatch(getProcedureOptions(tenantId));
    const conProducts = await dispatch(getProducts(tenantId));
    const conKits = await dispatch(getKits(tenantId));
    const conItems = await dispatch(getItems(true, tenantId));
    const conFlows = await dispatch(getFlows(tenantId));
    const conSets = await dispatch(getSets(tenantId));

    setConnectedSurgeons(conSurgeons || []);
    setConnectedHospitals(conHospitals || []);
    setConnectedProcedures(conProcedures || []);
    setConnectedProcedureOptions(conProcedureOptions || []);
    setConnectedProducts(conProducts || []);
    setConnectedKits(conKits || []);
    setConnectedItems(conItems || []);
    setConnectedFlows(conFlows || []);
    setConnectedSets(conSets || []);
  };

  useEffect(() => {
    if (formData?.products?.length) {
      fetchItems(formData.products).catch();
    } else {
      setItems([]);
    }
  }, [formData?.products]);

  const fetchItems = async (products) => {
    try {
      setFetching(true);
      const res = await dispatch(getItemsByProducts(products));
      setItems(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (activeCase?.additionalItems?.length) {
      fetchAdditionalItems(activeCase.additionalItems?.map((i) => i.id)).catch();
    } else {
      setAdditionalItems([]);
    }
  }, [activeCase?.additionalItems]);

  const fetchAdditionalItems = async (itemIds) => {
    try {
      setFetching(true);
      const res = await dispatch(getItemsByIds(itemIds));
      setAdditionalItems(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (formData.surgeon && formData.procedure && formData.hospital) {
      fetchKitPreference({
        surgeonId: formData.surgeon,
        procedureId: formData.procedure,
        hospitalId: formData.hospital,
      }).catch()
    }
  }, [formData.surgeon, formData.procedure, formData.hospital]);

  const fetchKitPreference = async (parameters) => {
    try {
      setKitPreferenceLoading(true);
      const res = await dispatch(getKitPreferenceByParameters(parameters));
      if (res && !!res.active) {
        setKitPreference(res);
        await fetchKitPreferenceItems(res?.items?.filter((i) => i.type === kitPreferenceItemTypes.ITEM.value)?.map((i) => i.value));
      } else {
        setKitPreference(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setKitPreferenceLoading(false);
    }
  };

  const fetchKitPreferenceItems = async (itemIds) => {
    try {
      setFetching(true);
      const res = await dispatch(getItemsByIds(itemIds));
      setKitPreferenceItems(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const procedure = procedures.find((p) => p.id === formData.procedure);
    if (formData.procedure && procedure && !!procedure.useEquipmentList) {
      fetchProcedureEquipmentLists(formData.procedure).catch();
    }
  }, [formData.procedure, procedures]);

  useEffect(() => {
    if (formData.procedureEquipmentList) {
      fetchProcedureEquipmentListItems(formData.procedureEquipmentList).catch()
    }
  }, [formData.procedureEquipmentList, procedureEquipmentLists]);

  const fetchProcedureEquipmentLists = async (procedureId) => {
    try {
      const res = await dispatch(getProcedureEquipmentLists(procedureId));
      setProcedureEquipmentLists(res || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProcedureEquipmentListItems = async (equipmentListId) => {
    try {
      setFetching(true);
      const equipmentList = procedureEquipmentLists.find((list) => list.id === equipmentListId);
      const itemIds = equipmentList?.items?.filter((i) => i.type === procedureEquipmentListItemTypes.ITEM.value)?.map((i) => i.value);
      const res = await dispatch(getItemsByIds(itemIds));
      setEquipmentListItems(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const handleClose = () => {
    onClose();
    setFormData({});

    if (mode === 'create' && userRole === userRoles.SALES_REP.name && !!connectedTenants?.length) {
      setStep('company');
    } else {
      setStep(0);
    }
  };

  const isConnected = useMemo(() => (
    userRole === userRoles.SALES_REP.name && ((mode === 'create' && selected !== currentTenant?.id) || connectedTenantId)
  ), [mode, userRole, selected, connectedTenantId]);

  const handleSubmit = async () => {
    startLoading();

    try {
      const result = await onSubmit(formData, isConnected ? selected : null);
      if (result !== false) {
        handleClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const goToKit = (kit) => {
    setSelectedKit(kit);
    dispatch(setStep('kit'));
  };

  const onBack = () => {
    dispatch(setStep(5));
    setSelectedKit(null);
  };

  const onSelectCompany = async () => {
    if (selected !== currentTenant?.id) {
      setFetching(true);

      try {
        await fetchConnectedData(selected);
      } catch (err) {
        console.error(err);
      } finally {
        setFetching(false);
      }
    }

    setStep(0);
  };

  const renderStep = () => {
    switch (step) {
      case 'company':
        return (
          <Step0
            currentTenant={currentTenant}
            tenants={tenants}
            connectedTenants={connectedTenants}
            loading={fetching}
            onClickNext={onSelectCompany}
            selected={selected}
            setSelected={setSelected}
          />
        )
      case 0:
        return (
          <Step1
            initialValues={formData}
            onClickNext={(formObj) => {
              if (formData?.procedure !== formObj?.procedure || formData?.hospital !== formObj?.hospital) {
                setFormData({ ...formData, ...formObj, kitPreference: null, procedureEquipmentList: null, products: [] });
              } else {
                setFormData({ ...formData, ...formObj });
              }
              setStep(1);
            }}
            onClose={handleClose}
            surgeons={isConnected ? connectedSurgeons : surgeons}
            hospitals={isConnected ? connectedHospitals : hospitals}
            procedures={isConnected ? connectedProcedures : procedures}
            procedureOptions={isConnected ? connectedProcedureOptions : procedureOptions}
            flows={isConnected ? connectedFlows : flows}
            flowSteps={flowSteps}
            status={activeCase.status}
            mode={mode}
            currentUser={currentUser}
          />
        );
      case 1:
        return <Step2
          initialValues={formData}
          onClickNext={(formObj) => {
            setFormData({ ...formData, ...formObj });
            setStep(2);
          }}
          onClickBack={() => setStep(0)}
          onClose={handleClose}
          products={isConnected ? connectedProducts : products}
          status={activeCase.status}
          procedures={isConnected ? connectedProcedures : procedures}
          procedureEquipmentLists={procedureEquipmentLists}
          kitPreference={kitPreference}
          kitPreferenceLoading={kitPreferenceLoading}
          orderClosed={activeCase.orderClosed}
        />;
      case 2:
        return <Step3
          initialValues={formData}
          onClickNext={(formObj, kitPreference) => {
            setFormData({ ...formData, ...formObj });
            if ((formObj.kitVariant === kitVariantTypes.consignment && (formObj.kits?.length || formObj.noEquipmentToShip)) || kitPreference) {
              setStep(3);
            } else {
              setStep(4);
            }
          }}
          onClickBack={() => setStep(1)}
          onClose={handleClose}
          kits={isConnected ? connectedKits : kits}
          items={isConnected ? connectedItems : [...items, ...kitPreferenceItems, ...equipmentListItems]}
          procedures={isConnected ? connectedProcedures : procedures}
          sets={isConnected ? connectedSets : sets}
          status={activeCase.status}
          procedureEquipmentLists={procedureEquipmentLists}
          tenantColor={tenantColor}
        />;
      case 3:
        return <Step3Additional
          initialValues={formData}
          onClickNext={(formObj) => {
            setFormData({ ...formData, ...formObj });
            setStep(4);
          }}
          onClickBack={() => setStep(2)}
          onClose={handleClose}
          kits={isConnected ? connectedKits : kits}
          items={isConnected ? connectedItems : [...items, ...kitPreferenceItems, ...equipmentListItems]}
          status={activeCase.status}
          procedureEquipmentLists={procedureEquipmentLists}
        />;
      case 4:
        return <Step4
          initialValues={formData}
          onClickNext={(formObj) => {
            setFormData({ ...formData, ...formObj });
            setStep(5);
          }}
          onClickBack={() => setStep(2)}
          onClose={handleClose}
          status={activeCase.status}
          hospitals={isConnected ? connectedHospitals : hospitals}
          userRole={userRole}
          userAddresses={userAddresses}
        />;
      case 5:
        return <Step5
          formData={formData}
          onSubmit={handleSubmit}
          onEditClick={setStep}
          onClickBack={() => setStep(4)}
          onClose={handleClose}
          surgeons={isConnected ? connectedSurgeons : surgeons}
          hospitals={isConnected ? connectedHospitals : hospitals}
          procedures={isConnected ? connectedProcedures : procedures}
          procedureOptions={isConnected ? connectedProcedures : procedureOptions}
          products={isConnected ? connectedProducts : products}
          kits={isConnected ? connectedKits : kits}
          items={isConnected ? connectedItems : [...items, ...kitPreferenceItems, ...additionalItems, ...equipmentListItems]}
          loading={loading}
          status={activeCase.status}
          editingBy={editingBy}
          tenantColor={tenantColor}
          goToKit={goToKit}
          procedureEquipmentLists={procedureEquipmentLists}
        />;
      case 'kit':
        return <KitBOM
          kit={selectedKit}
          onBack={onBack}
          kits={isConnected ? connectedKits : kits}
          items={isConnected ? connectedItems : [...items, ...kitPreferenceItems, ...additionalItems, ...equipmentListItems]}
          tenantId={isConnected ? selected : null}
        />;
      default:
        return null;
    }
  };

  const getFormattedId = useCallback((caseId) => {
    if (!caseId) {
      return ''
    }
    const arr = caseId?.split('-');
    return arr?.length > 1 ? `${arr[0]}-${arr[1]}` : caseId;
  }, []);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={mode === 'update' ? 'Edit Case' : 'Create Case'}
      subtitle={getFormattedId(activeCase.id)}
      fullWidth
      disableBackdropClick
      size='md'
      showCloseButton={false}
    >
      <div className='case-modal__container'>
        {renderStep()}
      </div>
    </Modal>
  );
};

export default CaseModal;
